import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  Button,
  Link,
  Backdrop
} from '@mui/material';

import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import InfoIcon from '@mui/icons-material/Info';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { apiClient } from '../api/apiClient';
import sanitizeHtml from '../utils/sanitizeHtml';
import { useAuth } from '../auth/AuthContext';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

const KATEGORIEN = [
  { code: 'R', name: 'Rechtsprechung' },
  { code: 'G', name: 'Gesetzgebung' },
  { code: 'O', name: 'Organisation' },
  { code: 'V', name: 'Veranstaltungen' },
  { code: 'S', name: 'Stellenausschreibungen' },
  { code: 'E', name: 'Entscheidungen' },
  { code: 'B', name: 'Bekanntmachungen' },
  { code: 'P', name: 'Pressemitteilungen' }
];

const MitteilungDetail = () => {
  const { id } = useParams();
  const { token } = useAuth();
  const [mitteilung, setMitteilung] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingFile, setDownloadingFile] = useState(false);

  useEffect(() => {
    const fetchMitteilung = async () => {
      try {
        setLoading(true);
        // Hole die Mitteilungsdetails
        const mitteilungResponse = await apiClient.get(`/mitteilungen/${id}`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        const mitteilungData = mitteilungResponse.data;
        
        // Hole die Dateinamen für alle angehängten Dateien
        const filePromises = Object.entries(mitteilungData.file_ids || {}).map(async ([fileId, uuid]) => {
          try {
            const fileResponse = await apiClient.get(`/files/mitteilungen/${fileId}`, {
              headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            });
            return [fileId, { uuid, ...fileResponse.data }];
          } catch (error) {
            console.error(`Fehler beim Laden der Dateiinfo für ${fileId}:`, error);
            return [fileId, { uuid, original_filename: `Datei-${fileId}` }];
          }
        });
        
        const fileResults = await Promise.all(filePromises);
        const fileInfos = Object.fromEntries(fileResults);
        
        // Kombiniere die Daten
        const completeData = {
          ...mitteilungData,
          fileInfos
        };
        
        setMitteilung(completeData);
        setError(null);
      } catch (error) {
        console.error('Fehler beim Laden der Mitteilung:', error);
        setError('Die Mitteilung konnte nicht geladen werden.');
      } finally {
        setLoading(false);
      }
    };

    fetchMitteilung();
  }, [id, token]);

  useEffect(() => {
    if (mitteilung?.titel) {
      document.title = `${mitteilung.titel} - Sozialgerichtsbarkeit`;
    } else {
      document.title = 'Mitteilung - Sozialgerichtsbarkeit';
    }

    return () => {
      document.title = 'Sozialgerichtsbarkeit';
    };
  }, [mitteilung]);

  const handleFileDownload = async (file) => {
    try {
      setDownloadingFile(true);
      const response = await apiClient.get(`/files/mitteilungen/download/${file.storage_filename}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        responseType: 'blob'
      });
      
      // Download initiieren
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Fehler beim Datei-Download:', error);
      setError('Die Datei konnte nicht heruntergeladen werden.');
    } finally {
      setDownloadingFile(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        width: '70%', 
        margin: '0 auto',
        p: 3,
        display: "flex", 
        justifyContent: "center", 
        alignItems: "center", 
        minHeight: "200px"
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ 
        width: '70%', 
        margin: '0 auto',
        p: 3
      }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!mitteilung) {
    return (
      <Box sx={{ 
        width: '70%', 
        margin: '0 auto',
        p: 3
      }}>
        <Typography>Mitteilung nicht gefunden</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: '70%', 
      margin: '0 auto',
      p: 3
    }}>
      {/* Basisinfos */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon color="primary" />
            Basisinfos
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary">
              Gericht
            </Typography>
            <Link
              component={RouterLink}
              to={`/gerichte/${mitteilung.gericht_id}`}
              sx={{ 
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                color: 'primary.main',
                mt: 0.5,
                '&:hover': { textDecoration: 'none', color: 'primary.dark' }
              }}
            >
              <AccountBalanceIcon />
              <Typography variant="h6">
                {mitteilung.gericht?.bezeichnung || '-'}
              </Typography>
            </Link>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="subtitle2" color="textSecondary">
              Kategorie
            </Typography>
            <Chip 
              label={KATEGORIEN.find(k => k.code === mitteilung.kategorie)?.name || mitteilung.kategorie}
              color="primary"
              sx={{ 
                mt: 0.5,
                height: 32,
                '& .MuiChip-label': {
                  fontSize: '1.15rem'
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="subtitle2" color="textSecondary">
              Gültigkeitszeitraum
            </Typography>
            <Typography variant="body1">
              {format(new Date(mitteilung.von_datum), 'dd.MM.yyyy', { locale: de })} - {' '}
              {format(new Date(mitteilung.bis_datum), 'dd.MM.yyyy', { locale: de })}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Text */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Text der Mitteilung
        </Typography>
        <Typography 
          variant="body1" 
          component="div"
          sx={{
            '& p': { marginBottom: 2 },
            '& br': { marginBottom: 1 }
          }}
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(mitteilung.text) }}
        />
      </Paper>

      {/* Angehängte Dateien */}
      {mitteilung.files && mitteilung.files.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Angehängte Dateien
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {mitteilung.files.map((file) => (
              <Button
                key={file.id}
                variant="outlined"
                color="primary"
                startIcon={<AttachFileIcon />}
                onClick={() => handleFileDownload(file)}
                disabled={downloadingFile}
              >
                {file.original_filename}
              </Button>
            ))}
          </Box>
        </Paper>
      )}

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading || downloadingFile}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default MitteilungDetail; 